import React, { useState } from 'react';
import { 
  Save, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  Download, 
  AlertCircle,
  Clock,
  Code,
  Image
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * SavedDecksDialog - Dialog for managing saved decks in localStorage
 * Allows viewing, loading, renaming, and deleting saved decks
 */
export default function SavedDecksDialog({
  open,
  onOpenChange,
  savedDecks,
  onLoad,
  onSave,
  onDelete,
  onRename,
  canSaveMore,
  maxDecks,
  currentDeckEmpty,
}) {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const handleStartRename = (deck) => {
    setEditingId(deck.id);
    setEditName(deck.name);
  };

  const handleSaveRename = () => {
    if (editName.trim() && editingId) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveCurrentDeck = () => {
    if (saveName.trim()) {
      onSave(saveName.trim());
      setSaveName('');
      setShowSaveInput(false);
    }
  };

  const handleLoad = (deck) => {
    onLoad(deck);
    onOpenChange(false);
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            {t('savedDecks.title')}
          </DialogTitle>
          <DialogDescription>
            {t('savedDecks.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Disclaimer Alert */}
        <Alert variant="default" className="bg-muted/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {t('savedDecks.disclaimer')}
          </AlertDescription>
        </Alert>

        {/* Save Current Deck Section */}
        {!currentDeckEmpty && (
          <div className="py-2 border-b">
            {showSaveInput ? (
              <div className="flex gap-2">
                <Input
                  placeholder={t('savedDecks.deckNamePlaceholder')}
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveCurrentDeck()}
                  autoFocus
                />
                <Button size="sm" onClick={handleSaveCurrentDeck} disabled={!saveName.trim()}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowSaveInput(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                className="w-full gap-2" 
                onClick={() => setShowSaveInput(true)}
                disabled={!canSaveMore}
              >
                <Save className="h-4 w-4" />
                {canSaveMore 
                  ? t('savedDecks.saveCurrentDeck')
                  : t('savedDecks.maxDecksReached', { maxDecks })}
              </Button>
            )}
          </div>
        )}

        {/* Saved Decks List */}
        <div className="flex-1 overflow-y-auto py-2 space-y-2 min-h-[200px]">
          {savedDecks.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <Save className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>{t('savedDecks.noDecks')}</p>
              <p className="text-sm mt-1">
                {t('savedDecks.noDecksHint')}
              </p>
            </div>
          ) : (
            savedDecks.map((deck) => (
              <div 
                key={deck.id} 
                className={`
                  flex items-center gap-3 p-3 rounded-lg border bg-card
                  hover:bg-accent/50 transition-colors
                  ${deck.isAutoSave ? 'border-dashed border-amber-500/50' : ''}
                `}
              >
                {/* Deck Info */}
                <div className="flex-1 min-w-0">
                  {editingId === deck.id ? (
                    <div className="flex gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRename();
                          if (e.key === 'Escape') handleCancelRename();
                        }}
                        autoFocus
                        className="h-8"
                      />
                      <Button size="sm" variant="ghost" onClick={handleSaveRename}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelRename}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{deck.name}</span>
                        {deck.isAutoSave && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            {deck.autoSaveType === 'exportCode' ? (
                              <Code className="h-3 w-3" />
                            ) : deck.autoSaveType === 'exportImage' ? (
                              <Image className="h-3 w-3" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                            {t('savedDecks.autoSave')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>{deck.cardCount || '?'} cards</span>
                        <span>•</span>
                        <span>{formatDate(deck.savedAt)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                {editingId !== deck.id && (
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" onClick={() => handleLoad(deck)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('savedDecks.load')}</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" onClick={() => handleStartRename(deck)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('savedDecks.rename')}</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => onDelete(deck.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('savedDecks.delete')}</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
